import { useState, useRef, useCallback, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/utils/cn'
import { formatUserDisplayName } from '@/utils/formatters'
import { getInitials, getAvatarColor } from '@/utils/avatarUtils'
import { useUserSearch } from '@/hooks/useUserSearch'
import { useTransferAdminPlanOwnership } from '@/hooks/useAdminPlans'
import type { UserRead } from '@/api/shares'
import type { ApiPlanSummary } from '@/types'

interface TransferOwnershipDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  plan: ApiPlanSummary | null
}

export function TransferOwnershipDialog({
  open,
  onOpenChange,
  plan,
}: TransferOwnershipDialogProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUser, setSelectedUser] = useState<UserRead | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [keepAsEditor, setKeepAsEditor] = useState(true)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const transferMutation = useTransferAdminPlanOwnership()

  const {
    data: searchData,
    isLoading: isSearching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useUserSearch({ query: searchQuery })

  // Flatten pages and filter out current owner
  const allUsers = searchData?.pages.flatMap(page => page.users) ?? []
  const users = plan?.owner
    ? allUsers.filter(u => u.id !== plan.owner?.id)
    : allUsers

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('')
      setSelectedUser(null)
      setShowDropdown(false)
      setKeepAsEditor(true)
    }
  }, [open])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!listRef.current || !hasNextPage || isFetchingNextPage) return

    const { scrollTop, scrollHeight, clientHeight } = listRef.current
    if (scrollHeight - scrollTop - clientHeight < 100) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setSelectedUser(null)
    setShowDropdown(true)
  }

  const handleInputFocus = () => {
    if (searchQuery.length >= 2) {
      setShowDropdown(true)
    }
  }

  const handleSelectUser = (user: UserRead) => {
    setSelectedUser(user)
    setSearchQuery(user.email || user.display_name || '')
    setShowDropdown(false)
  }

  const handleTransfer = () => {
    if (!plan || !selectedUser) return

    transferMutation.mutate(
      {
        planId: plan.id,
        newOwnerId: selectedUser.id,
        keepAsEditor,
      },
      {
        onSuccess: () => {
          onOpenChange(false)
        },
      }
    )
  }

  const shouldShowDropdown = showDropdown && searchQuery.length >= 2

  if (!plan) return null

  return (
    <Dialog
      open={open}
      onOpenChange={transferMutation.isPending ? undefined : onOpenChange}
    >
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Transfer Ownership</DialogTitle>
          <DialogDescription>
            Transfer ownership of &ldquo;{plan.name}&rdquo;
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Current owner */}
          <div className="space-y-1">
            <Label className="text-sm text-muted-foreground">
              Current Owner
            </Label>
            <p className="text-sm font-medium">
              {formatUserDisplayName(plan.owner)}
            </p>
          </div>

          {/* New owner search */}
          <div className="space-y-2">
            <Label>New Owner</Label>
            <div className="relative" ref={dropdownRef}>
              <Input
                ref={inputRef}
                placeholder="Search by email or name..."
                value={searchQuery}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                className="w-full"
              />

              {shouldShowDropdown && (
                <div
                  ref={listRef}
                  onScroll={handleScroll}
                  className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-md shadow-lg max-h-[240px] overflow-y-auto z-50"
                >
                  {isSearching && !isFetchingNextPage ? (
                    <div className="px-4 py-6 text-center text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                      <span className="text-sm">Searching...</span>
                    </div>
                  ) : users.length > 0 ? (
                    <ul className="py-1">
                      {users.map(user => {
                        const colors = getAvatarColor(user.id)
                        return (
                          <li key={user.id}>
                            <button
                              type="button"
                              onClick={() => handleSelectUser(user)}
                              className={cn(
                                'w-full flex items-center gap-3 px-3 py-2',
                                'hover:bg-muted cursor-pointer',
                                'text-left transition-colors duration-150',
                                selectedUser?.id === user.id && 'bg-muted'
                              )}
                            >
                              <Avatar className="h-8 w-8">
                                <AvatarFallback
                                  className={cn(
                                    colors.bg,
                                    colors.text,
                                    'text-xs font-medium'
                                  )}
                                >
                                  {getInitials(user.display_name, user.email)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {user.display_name || user.email}
                                </p>
                                {user.display_name && user.email && (
                                  <p className="text-xs text-muted-foreground truncate">
                                    {user.email}
                                  </p>
                                )}
                              </div>
                            </button>
                          </li>
                        )
                      })}
                      {isFetchingNextPage && (
                        <li className="px-4 py-3 text-center">
                          <Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" />
                        </li>
                      )}
                    </ul>
                  ) : (
                    <div className="px-4 py-6 text-center text-muted-foreground">
                      <span className="text-sm">
                        No users found for &ldquo;{searchQuery}&rdquo;
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Keep as editor checkbox */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="keep-as-editor"
              checked={keepAsEditor}
              onCheckedChange={checked => setKeepAsEditor(checked === true)}
            />
            <label
              htmlFor="keep-as-editor"
              className="cursor-pointer text-sm leading-none"
            >
              Keep current owner as an editor
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={transferMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleTransfer}
            disabled={!selectedUser || transferMutation.isPending}
          >
            {transferMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Transfer'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
